import { useEffect, useRef, useState, type FormEvent, type JSX } from 'react';
import { api, type AiTurn } from '../api.ts';
import { Markup } from './Markup.tsx';

/**
 * The study assistant, beside the lesson it answers about.
 *
 * Closed until asked for. An open chat box on every lesson invites somebody to ask before they
 * have read anything, and this exists to unstick a learner, not to replace the lesson.
 *
 * What it will not do is said out loud, once, at the top: it answers from this course's material,
 * and it does not hand over homework answers. A tool whose limits are discovered by disappointment
 * gets abandoned; one that states them gets used for what it is good at.
 */

/** Long enough for a real question, short enough that it stays a question rather than an essay. */
const MAX_CHARS = 1_000;

export function AiHelper({ lessonId }: { lessonId: string }): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [turns, setTurns] = useState<AiTurn[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [remaining, setRemaining] = useState<number | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;

    api
      .aiConversation(lessonId)
      .then((data) => {
        if (!alive) return;

        setEnabled(data.enabled);
        setTurns(data.messages.filter((m) => m.role !== 'system'));
      })
      .catch(() => alive && setEnabled(false));

    return () => {
      alive = false;
    };
  }, [lessonId]);

  // A conversation is read from the bottom: the newest line is the one you came for.
  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ block: 'end' });
  }, [turns, open]);

  async function send(event: FormEvent): Promise<void> {
    event.preventDefault();

    const question = draft.trim();
    if (!question || busy) return;

    setBusy(true);
    setError('');

    /*
     * The question appears at once, before the answer arrives. A question that vanishes for ten
     * seconds looks like it was lost, and the natural response is to type it again.
     */
    setTurns((current) => [
      ...current,
      { role: 'user', content: question, created_at: new Date().toISOString() },
    ]);
    setDraft('');

    try {
      const answer = await api.askAi(lessonId, question);

      setTurns((current) => [
        ...current,
        { role: 'assistant', content: answer.text, created_at: new Date().toISOString() },
      ]);
      setRemaining(answer.remaining_today);
    } catch (cause) {
      // The question stays on screen: it was asked, and losing it would mean typing it again.
      setError(cause instanceof Error ? cause.message : 'Помощник не ответил');
    } finally {
      setBusy(false);
    }
  }

  // Nothing at all until we know: a button that might be broken is worse than a moment's wait.
  if (enabled === null) return null;

  if (!enabled) {
    return (
      <div className="helper helper--off">
        <h3>Помощник по уроку</h3>
        <p className="muted">Пока не подключён.</p>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="helper">
        <h3>Не понятно?</h3>
        <p className="muted">
          Спросите помощника — он объяснит по материалам этого курса. Домашнее задание за вас он не
          решает.
        </p>
        <button type="button" className="btn btn--small" onClick={() => setOpen(true)}>
          Спросить
        </button>
      </div>
    );
  }

  return (
    <div className="helper helper--open">
      <div className="helper__head">
        <h3>Помощник по уроку</h3>
        <button type="button" className="linklike" onClick={() => setOpen(false)}>
          свернуть
        </button>
      </div>

      <div className="helper__log">
        {turns.length === 0 ? (
          <p className="muted">
            Спросите про то, что не сошлось. Например: «объясни ещё раз, зачем нужен цикл».
          </p>
        ) : null}

        {turns.map((turn, index) => (
          <div
            key={`${turn.created_at}-${index}`}
            className={`bubble${turn.role === 'user' ? ' bubble--mine' : ''}`}
          >
            {turn.role === 'assistant' ? (
              <Markup text={turn.content} />
            ) : (
              <div className="bubble__body">{turn.content}</div>
            )}
          </div>
        ))}

        {busy ? <p className="muted helper__thinking">Читаю материалы урока…</p> : null}
        <div ref={endRef} />
      </div>

      {error ? <p className="error">{error}</p> : null}

      <form className="helper__form" onSubmit={(e) => void send(e)}>
        <textarea
          className="input"
          rows={2}
          value={draft}
          maxLength={MAX_CHARS}
          disabled={busy}
          placeholder="Что именно не понятно?"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends, Shift+Enter starts a new line — what a chat box already means to
            // everybody, and what stops half a question going out mid-thought.
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send(e);
            }
          }}
        />

        <div className="row">
          <button className="btn btn--small" type="submit" disabled={busy || !draft.trim()}>
            {busy ? 'Думаю…' : 'Спросить'}
          </button>

          {remaining !== null ? (
            <span className="muted" style={{ fontSize: '0.8rem' }}>
              осталось вопросов сегодня: {remaining}
            </span>
          ) : null}
        </div>
      </form>
    </div>
  );
}
